import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, finalize, switchMap, takeUntil } from 'rxjs/operators';
import {
  PlanejamentoAula,
  PlanejamentoAulaApiItem,
  PlanejamentoCampoAdicionalItem,
  PlanejamentoItemConteudo,
  PlanejamentoObservacaoItem,
  PlanejamentoSituacaoAprovacao,
  normalizarCamposAdicionais
} from '../models/planejamento-aula.types';
import { PlanejamentoAulaService } from '../services/planejamento-aula.service';
import { getSituacaoLabel, getSituacaoBadgeClass, formatarLabelAula } from '../utils/planejamento-aula.utils';

interface DetalhamentoItem {
  label: string;
  valor: string;
  expandido?: boolean;
}

/** Labels padrão usadas quando a configuração da escola não define uma descrição. */
const LABEL_PADRAO: Record<string, string> = {
  ANP:                   'ANP',
  DEVOLUTIVA:            'Criar devolutiva?',
  CARGA_HORARIA:         'Carga horária',
  TEMA:                  'Tema',
  OBJETIVOS_GERAIS:      'Objetivos gerais',
  OBJETIVOS_ESPECIFICOS: 'Objetivos específicos',
  METODOLOGIA:           'Metodologia utilizada',
  PRATICAS:              'Práticas pedagógicas',
  RECURSOS:              'Recursos didáticos',
  REFERENCIAS:           'Referências bibliográficas'
};

/** Ordem de exibição e mapeamento chave→tipo para o detalhamento. */
const ITEM_CONFIGS: ReadonlyArray<{ key: string; type: string }> = [
  { key: 'anp',                       type: 'ANP'                   },
  { key: 'isDevolutiva',              type: 'DEVOLUTIVA'            },
  { key: 'cargaHoraria',              type: 'CARGA_HORARIA'         },
  { key: 'tema',                      type: 'TEMA'                  },
  { key: 'objetivosGerais',           type: 'OBJETIVOS_GERAIS'      },
  { key: 'objetivosEspecificos',      type: 'OBJETIVOS_ESPECIFICOS' },
  { key: 'metodologiaUtilizada',      type: 'METODOLOGIA'           },
  { key: 'praticasPedagogicas',       type: 'PRATICAS'              },
  { key: 'recursosDidaticos',         type: 'RECURSOS'              },
  { key: 'referenciasBibliograficas', type: 'REFERENCIAS'           }
];

@Component({
  selector: 'app-planejamento-aula-detail',
  templateUrl: './planejamento-aula-detail.component.html',
  styleUrls: ['./planejamento-aula-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlanejamentoAulaDetailComponent implements OnChanges, OnDestroy {
  private destroy$ = new Subject<void>();

  @Input() aberta = false;
  @Input() registro: PlanejamentoAula | null = null;
  @Input() turmaNome = '';

  @Output() fechar = new EventEmitter<void>();

  carregandoDetalhe = false;
  erroDetalhe = false;
  registroCompleto: PlanejamentoAulaApiItem | null = null;
  camposAdicionaisList: PlanejamentoCampoAdicionalItem[] = [];
  detalhamentoList: DetalhamentoItem[] = [];
  configLabels: Record<string, string> = {};
  configuracoesOriginal: any = null;

  constructor(
    private planejamentoService: PlanejamentoAulaService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['aberta'] && this.aberta && this.registro) {
      this.carregarDetalhes(this.registro.id);
    }
    if (changes['aberta'] && !this.aberta) {
      this.resetarEstado();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Getters de dados resolvidos ──────────────────────────────────────────


  get turmas(): string[] {
    const turmasApi = this.registroCompleto?.turmas;
    if (turmasApi?.length) {
      return turmasApi.map(t => t.descricao || '').filter(Boolean);
    }
    return this.turmaNome ? [this.turmaNome] : [];
  }


  get observacao(): string | null {
    return (this.registroCompleto?.observacao || this.registro?.observacao) ?? null;
  }

  get listaAnexos(): any[] {
    const rec = this.registroCompleto as any;
    return rec?.arquivos?.length ? rec.arquivos : (rec?.anexos ?? []);
  }

  get possuiAnexos(): boolean {
    return this.listaAnexos.length > 0;
  }

  get observacoesAprovador(): PlanejamentoObservacaoItem[] {
    return (this.registroCompleto?.observacoesAprovacaoConteudos ?? [])
      .filter(obs => obs.situacao !== 'INATIVO');
  }

  // ─── Badge de situação ────────────────────────────────────────────────────

  get situacaoAtual(): PlanejamentoSituacaoAprovacao | null {
    return this.registroCompleto?.situacaoAprovacao ?? this.registro?.situacaoAprovacaoRaw ?? null;
  }

  get exibirSituacao(): boolean {
    const sit = this.situacaoAtual;
    if (!sit) return false;
    const str = String(sit).trim().toLowerCase();
    return str !== 'null' && str !== '';
  }

  getSituacaoLabel      = getSituacaoLabel;
  getSituacaoBadgeClass = getSituacaoBadgeClass;
  formatarLabelAula     = formatarLabelAula;


  // ─── Helpers de arquivo ──────────────────────────────────────────────────

  obterNomeSemExtensao(nomeCompleto: string | null | undefined): string {
    if (!nomeCompleto) return '';
    const partes = nomeCompleto.split('.');
    if (partes.length === 1) return nomeCompleto;
    partes.pop();
    return partes.join('.');
  }

  obterExtensao(nomeCompleto: string | null | undefined): string {
    if (!nomeCompleto) return '';
    const partes = nomeCompleto.split('.');
    if (partes.length === 1) return '';
    return '.' + partes.pop();
  }

  // ─── Helpers de UI (expand/collapse) ─────────────────────────────────────

  toggleExpand(item: DetalhamentoItem, cardElement: HTMLElement): void {
    item.expandido = !item.expandido;
    setTimeout(() => cardElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
  }

  /** Alterna a expansão da turma. Ao fechar, reseta "ver mais aulas" para limpeza visual. */
  toggleTurmaExpand(item: PlanejamentoItemConteudo): void {
    item.expandido = !item.expandido;
    if (!item.expandido) {
      item.expandirAulas = false;
    }
  }

  // ─── TrackBy helpers (performance de *ngFor) ──────────────────────────────

  trackById(_index: number, item: any): any {
    return item?.id ?? _index;
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByData(_index: number, grupo: { data: string }): string {
    return grupo.data;
  }

  getTotalAulas(itemConteudo: PlanejamentoItemConteudo): number {
    return (itemConteudo?.aulasPlanejadas?.length || 0) + (itemConteudo?.aulas?.length || 0);
  }

  getComponentesComAulas(itemConteudo: any, limit?: number): any[] {
    const componentesMap = new Map<number, any>();

    (itemConteudo?.itensEducacionais || []).forEach((ie: any) => {
      const itemEd = ie?.itemEducacional;
      if (itemEd?.id) {
        componentesMap.set(itemEd.id, {
          nome: itemEd.descricao || itemEd.nome || 'Não informado',
          porData: []
        });
      }
    });

    const todasAulas = itemConteudo?.aulasPlanejadas?.length ? itemConteudo.aulasPlanejadas : (itemConteudo?.aulas || []);
    const listaAulas = limit ? todasAulas.slice(0, limit) : todasAulas;

    listaAulas.forEach((aula: any) => {
      const idComp = aula?.itemEducacional?.id;
      const nomeComp = aula?.itemEducacional?.descricao || aula?.itemEducacional?.nome || 'Não informado';
      const dataAula = aula?.data;

      if (idComp && !componentesMap.has(idComp)) {
        componentesMap.set(idComp, { nome: nomeComp, porData: [] });
      }

      const comp = componentesMap.get(idComp);
      if (!comp) return;

      let grupo = comp.porData.find((g: any) => g.data === dataAula);
      if (!grupo) {
        grupo = { data: dataAula, itens: [] };
        comp.porData.push(grupo);
      }
      grupo.itens.push(aula);
    });

    return Array.from(componentesMap.values())
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' }));
  }


  // ─── Carregamento ─────────────────────────────────────────────────────────

  private carregarDetalhes(id: number): void {
    this.carregandoDetalhe = true;
    this.erroDetalhe       = false;
    this.registroCompleto  = null;
    this.camposAdicionaisList = [];
    this.configLabels = {};

    this.planejamentoService.getRegistroCompleto(id).pipe(
      takeUntil(this.destroy$),
      switchMap(completo => {
        this.registroCompleto = {
          ...completo,
          itensConteudos: completo.itensConteudos?.map(item => ({
            ...item,
            expandido:     false,
            expandirAulas: false
          }))
        };

        const idTurmaConfig = completo.itensConteudos?.[0]?.turma?.id
          ?? completo.turmas?.[0]?.id;

        return forkJoin({
          campos: this.planejamentoService.getCamposAdicionais(id).pipe(
            catchError(() => of(null))
          ),
          configuracoes: idTurmaConfig
            ? this.planejamentoService.getConfiguracoesEstabelecimento(idTurmaConfig).pipe(
                catchError(() => of(null))
              )
            : of(null)
        });
      }),
      finalize(() => { this.carregandoDetalhe = false; })
    ).subscribe({
      next:  ({ campos, configuracoes }) => this.processarDetalhes(campos, configuracoes),
      error: () => { this.erroDetalhe = true; }
    });
  }

  private processarDetalhes(campos: any, configuracoes: any): void {
    this.camposAdicionaisList  = normalizarCamposAdicionais(campos);
    this.configuracoesOriginal = this.resolverConfigPrincipal(configuracoes);
    this.configLabels          = this.resolverConfigLabels(this.configuracoesOriginal);

    this.buildDetalhamentoList();
    this.cdr.markForCheck();
  }

  /** Normaliza a resposta de configurações (objeto direto, array ou resposta paginada). */
  private resolverConfigPrincipal(configuracoes: any): any {
    if (!configuracoes)          return null;
    if (Array.isArray(configuracoes)) return configuracoes[0] ?? null;
    if (configuracoes.content)   return configuracoes.content[0] ?? null;
    return configuracoes;
  }

  /**
   * Constrói o mapa de labels customizadas.
   * Parte dos defaults de LABEL_PADRAO e sobrescreve apenas os campos
   * em que o estabelecimento definiu uma descrição personalizada.
   */
  private resolverConfigLabels(configPrincipal: any): Record<string, string> {
    const itens: any[] = configPrincipal?.configuracaoPlanejamentoAulaItens ?? [];
    const labels: Record<string, string> = { ...LABEL_PADRAO };

    itens.forEach((c: any) => {
      if (c?.itemPlanejamentoAula && c?.descricao) {
        labels[c.itemPlanejamentoAula] = c.descricao;
      }
    });

    return labels;
  }

  private buildDetalhamentoList(): void {
    if (!this.registroCompleto) {
      this.detalhamentoList = [];
      return;
    }

    this.detalhamentoList = ITEM_CONFIGS
      .filter(conf => this.campoEstaVisivel(conf.type))
      .map(conf  => this.montarItemDetalhamento(conf))
      .filter((item): item is DetalhamentoItem => item !== null);
  }

  /** Retorna false quando a configuração do estabelecimento marca o campo como oculto. */
  private campoEstaVisivel(type: string): boolean {
    const configItem = this.configuracoesOriginal?.configuracaoPlanejamentoAulaItens
      ?.find((c: any) => c.itemPlanejamentoAula === type);
    return !configItem || configItem.visivel !== false;
  }

  /** Resolve valor e label de um campo, retornando null se o valor for vazio. */
  private montarItemDetalhamento(conf: { key: string; type: string }): DetalhamentoItem | null {
    const valor: any = conf.type === 'CARGA_HORARIA'
      ? this.formatarCargaHoraria()
      : (this.registroCompleto as any)[conf.key];

    if (valor === null || valor === undefined || valor === '') return null;

    return {
      label: this.configLabels[conf.type] ?? LABEL_PADRAO[conf.type] ?? conf.key,
      valor: this.formatarValorItem(valor, conf.type)
    };
  }

  private formatarCargaHoraria(): string | null {
    const rec = this.registroCompleto as any;
    if (rec?.['horas'] == null && rec?.['minutos'] == null) return null;
    const h = (rec['horas']   as number) ?? 0;
    const m = (rec['minutos'] as number) ?? 0;
    return `${h}:${String(m).padStart(2, '0')}`;
  }

  private formatarValorItem(valor: any, type: string): string {
    if (typeof valor === 'boolean') return valor ? 'Sim' : 'Não';
    if (type === 'ANP' && String(valor).toUpperCase() === 'HIBRIDO') return 'Híbrido';
    return String(valor);
  }

  private resetarEstado(): void {
    this.registroCompleto      = null;
    this.camposAdicionaisList  = [];
    this.detalhamentoList      = [];
    this.configLabels          = {};
    this.configuracoesOriginal = null;
    this.erroDetalhe           = false;
  }
}

